import type { Immutable } from "immer";
import React, { useContext } from "react";
import { ChatLink } from "tsi-common-react/src/components/ChatLink/index";
import { isoWebPageURL } from "tsi-common-react/src/models/nominals";
import { getReactivatedContext } from "tsi-common-react/src/utils/environment";
import { usePageURL } from "tsi-common-react/src/utils/urls";
import { t } from "ttag";

import { Context } from "@reactivated";
import type { templates } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

/**
 * Faithful port of the legacy server-rendered
 * ``cms/blocks/customer-service-info-block.html`` (a StaticBlock).
 *
 * The whole ``<section class="customer-service-info-block">`` wrapper moves into
 * React, including the two ``__card`` blocks built from
 * ``CustomerServiceSettings`` / ``FooterSettings`` (phones + hours) and the two
 * static badge icons. The request/settings-dependent data is supplied by the
 * ``customer_service_info_block_data`` templatetag (tempur_tags.py); no block
 * value exists (BreadcrumbsBlock precedent).
 *
 * The chat link stays a REAL interactive hydration island: the legacy template
 * rendered it inline via ``{% include_react "...ChatLinkTemplate" %}`` (a
 * Zendesk-connected button / contact-us link), so the shared ``ChatLink``
 * component is rendered directly as a child here (NOT baked into an html
 * string, which would render but never hydrate). The legacy args were
 * ``class_name=""``, ``chat_online_text="Chat"``, ``chat_offline_text="Contact
 * us"``, ``chat_offline_link=urls.contact_us_form``, ``hide_if_offline=False``;
 * the offline-link fallback (when no contact-us-form page) mirrors
 * ChatLinkTemplate's ``usePageURL("home")``.
 *
 * Static badge icons use the reactivated ``STATIC_URL`` context to rebuild the
 * legacy ``{% static_image %}`` src (ComparisonBlock precedent); ``alt=""`` as
 * in the legacy tag. Hours HTML becomes ``<li>`` children via
 * ``hoursHtmlToListItems``.
 */

/** Map Wagtail richtext hours HTML into ``<li>`` items for a real list. */
const hoursHtmlToListItems = (html: string): string =>
    html
        .split(/<\/?(?:div|p)\b[^>]*>|<br\s*\/?>/gi)
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => `<li>${part}</li>`)
        .join("");

export const CustomerServiceInfoBlock = (
    props: Immutable<templates.CustomerServiceInfoBlockTemplate>,
): React.JSX.Element => {
    const context = useContext(Context) || getReactivatedContext();
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const staticURL = context?.STATIC_URL ?? "/static/";
    const home = usePageURL("home");
    const chatOfflineLink = props.chat_offline_link
        ? isoWebPageURL.wrap(props.chat_offline_link)
        : home;
    return (
        <section
            // "customer-service-info-block" is kept as a hook for server/cms/tests (*_documents.py)
            className={concatClassNames([
                styles.customerServiceInfoBlock,
                "customer-service-info-block",
            ])}
        >
            <div className={styles.customerServiceInfoBlockIntro}>
                <h2>{t`Need assistance? We're here to help.`}</h2>
                <div className={styles.combinedCopy}>
                    {t`We want to hear from you, whether you have a problem, comment, or question.`}{" "}
                    <ChatLink
                        className={undefined}
                        chatOnlineText={t`Chat`}
                        chatOfflineText={t`Contact us`}
                        chatOfflineLink={chatOfflineLink}
                        hideIfOffline={false}
                    />{" "}
                    {t`or give us a call to speak with a sleep expert today.`}
                </div>
            </div>

            <div
                // "customer-service-info-block__card" is kept as a hook for server/cms/tests (*_documents.py)
                className={concatClassNames([
                    styles.customerServiceInfoBlockCard,
                    "customer-service-info-block__card",
                ])}
            >
                <img
                    src={`${staticURL}img/icons/badge-sleep-expert.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>
                    <strong>{t`Sleep Expert`}</strong>
                </h3>
                <p>{t`Evaluating our products and need advice before you buy?`}</p>
                <a href={props.pre_order_phone_rfc3966}>
                    <h3>{props.pre_order_phone_national}</h3>
                </a>
                <ul
                    // "customer-service-info-block__hours" is kept as a hook for server/cms/tests (*_documents.py)
                    className={concatClassNames([
                        styles.customerServiceInfoBlockHours,
                        "customer-service-info-block__hours",
                    ])}
                    role="list"
                    dangerouslySetInnerHTML={{
                        __html: hoursHtmlToListItems(
                            props.talk_modal_hours_top_html,
                        ),
                    }}
                />
            </div>

            <div
                // "customer-service-info-block__card" is kept as a hook for server/cms/tests (*_documents.py)
                className={concatClassNames([
                    styles.customerServiceInfoBlockCard,
                    "customer-service-info-block__card",
                ])}
            >
                <img
                    src={`${staticURL}img/icons/badge-headset.png`}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
                <h3>
                    <strong>{t`Customer Support`}</strong>
                </h3>
                <p>{t`Already using our products or need assistance with an order you placed?`}</p>
                <a href={props.customer_service_phone_rfc3966}>
                    <h3>{props.customer_service_phone_national}</h3>
                </a>
                <ul
                    // "customer-service-info-block__hours" is kept as a hook for server/cms/tests (*_documents.py)
                    className={concatClassNames([
                        styles.customerServiceInfoBlockHours,
                        "customer-service-info-block__hours",
                    ])}
                    role="list"
                    dangerouslySetInnerHTML={{
                        __html: hoursHtmlToListItems(
                            props.talk_modal_hours_bottom_html,
                        ),
                    }}
                />
            </div>
        </section>
    );
};
